
import React, { useState, useEffect } from 'react';
import { MedicationReminder, Appointment } from '../types';
import { supabase } from '../services/supabase';
import Swal from 'sweetalert2';

const Alerts: React.FC = () => {
  const [reminders, setReminders] = useState<MedicationReminder[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showAddApp, setShowAddApp] = useState(false);
  
  const [newMed, setNewMed] = useState({ name: '', time: '08:00', dosage: '' });
  const [newApp, setNewApp] = useState({ date: new Date().toISOString().split('T')[0], time: '09:00', clinic: '', notes: '' });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch Medications
      const { data: medsData, error: medsError } = await supabase
        .from('medication_reminders')
        .select('*')
        .eq('user_id', user.id)
        .order('time', { ascending: true });

      // Fetch Appointments
      const { data: appsData, error: appsError } = await supabase
        .from('appointments')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: true });

      if (medsError) console.error("Error fetching medications:", medsError);
      if (appsError) console.error("Error fetching appointments:", appsError);

      if (medsData) setReminders(medsData);
      if (appsData) setAppointments(appsData);
    } catch (err) {
      console.error("Critical error in Alerts component:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMed = async () => {
    if (!newMed.name) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const med: Partial<MedicationReminder> = {
        user_id: user.id,
        name: newMed.name,
        time: newMed.time,
        dosage: newMed.dosage,
        days: ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'],
        active: true
      };

      const { error } = await supabase.from('medication_reminders').insert([med]);
      if (error) throw error;

      setShowAddMed(false);
      setNewMed({ name: '', time: '08:00', dosage: '' });
      Swal.fire({ title: 'เพิ่มรายการยาแล้ว', icon: 'success', timer: 1500, showConfirmButton: false });
      fetchAllData();
    } catch (err: any) {
      Swal.fire({ title: 'ล้มเหลว', text: err.message, icon: 'error' });
    }
  };

  const handleAddApp = async () => {
    if (!newApp.clinic || !newApp.date) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const app: Partial<Appointment> = {
        user_id: user.id,
        date: newApp.date,
        time: newApp.time,
        clinic: newApp.clinic,
        notes: newApp.notes
      };

      const { error } = await supabase.from('appointments').insert([app]);
      if (error) throw error;

      setShowAddApp(false);
      setNewApp({ date: new Date().toISOString().split('T')[0], time: '09:00', clinic: '', notes: '' });
      Swal.fire({ title: 'บันทึกนัดหมายแล้ว', icon: 'success', timer: 1500, showConfirmButton: false });
      fetchAllData();
    } catch (err: any) {
      Swal.fire({ title: 'ล้มเหลว', text: err.message, icon: 'error' });
    }
  };

  const handleDeleteMed = async (id: string) => {
    const result = await Swal.fire({
      title: 'ลบรายการยานี้?',
      text: 'คุณจะไม่ได้รับการแจ้งเตือนสำหรับยานี้อีก',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8'
    });

    if (result.isConfirmed) {
      const { error } = await supabase.from('medication_reminders').delete().eq('id', id);
      if (!error) {
        setReminders(reminders.filter(r => r.id !== id));
        Swal.fire({ title: 'ลบแล้ว', icon: 'success', timer: 1000, showConfirmButton: false });
      }
    }
  };

  const handleDeleteApp = async (id: string) => {
    const result = await Swal.fire({
      title: 'ลบนัดหมาย?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8'
    });

    if (result.isConfirmed) {
      const { error } = await supabase.from('appointments').delete().eq('id', id);
      if (!error) {
        setAppointments(appointments.filter(a => a.id !== id));
        Swal.fire({ title: 'ลบแล้ว', icon: 'success', timer: 1000, showConfirmButton: false });
      }
    }
  };

  const toggleMedActive = async (id: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from('medication_reminders')
      .update({ active: !currentStatus })
      .eq('id', id);
    
    if (!error) {
      setReminders(reminders.map(r => r.id === id ? { ...r, active: !currentStatus } : r));
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">แจ้งเตือนยา & นัดหมาย</h2>
        <div className="flex gap-2">
          <button 
            onClick={() => { setShowAddMed(!showAddMed); setShowAddApp(false); }} 
            className={`p-2 rounded-xl text-xs font-bold transition-all ${showAddMed ? 'bg-blue-600 text-white' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'}`}
          >
            {showAddMed ? 'ปิด' : '+ ยา'}
          </button>
          <button 
            onClick={() => { setShowAddApp(!showAddApp); setShowAddMed(false); }} 
            className={`p-2 rounded-xl text-xs font-bold transition-all ${showAddApp ? 'bg-indigo-600 text-white' : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'}`}
          >
            {showAddApp ? 'ปิด' : '+ นัด'}
          </button>
        </div>
      </div>

      {showAddMed && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border-2 border-blue-50 dark:border-slate-800 shadow-xl space-y-4 animate-in slide-in-from-top-4 duration-300">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ชื่อรายการยา</label>
            <input className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:border-blue-500 transition-all text-slate-700 dark:text-slate-200" placeholder="เช่น ยาลดความดัน" value={newMed.name} onChange={e => setNewMed({...newMed, name: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">เวลาทาน</label>
              <input type="time" className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" value={newMed.time} onChange={e => setNewMed({...newMed, time: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ปริมาณ (Dosage)</label>
              <input className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" placeholder="เช่น 1 เม็ด" value={newMed.dosage} onChange={e => setNewMed({...newMed, dosage: e.target.value})} />
            </div>
          </div>
          <button onClick={handleAddMed} className="w-full py-4 bg-blue-600 dark:bg-blue-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-100 dark:shadow-none active:scale-95 transition-all">บันทึกยา</button>
        </div>
      )}

      {showAddApp && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border-2 border-indigo-50 dark:border-slate-800 shadow-xl space-y-4 animate-in slide-in-from-top-4 duration-300">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">วันที่นัด</label>
              <input type="date" className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" value={newApp.date} onChange={e => setNewApp({...newApp, date: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">เวลานัด</label>
              <input type="time" className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" value={newApp.time} onChange={e => setNewApp({...newApp, time: e.target.value})} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ชื่อคลินิก/โรงพยาบาล</label>
            <input className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" placeholder="เช่น คลินิกเบาหวาน" value={newApp.clinic} onChange={e => setNewApp({...newApp, clinic: e.target.value})} />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">หมายเหตุ</label>
            <input className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none text-slate-700 dark:text-slate-200" placeholder="เช่น ตึกใหม่ ชั้น 2" value={newApp.notes} onChange={e => setNewApp({...newApp, notes: e.target.value})} />
          </div>
          <button onClick={handleAddApp} className="w-full py-4 bg-indigo-600 dark:bg-indigo-500 text-white font-bold rounded-2xl shadow-lg shadow-indigo-100 dark:shadow-none active:scale-95 transition-all">บันทึกนัดหมาย</button>
        </div>
      )}

      {/* Appointments List */}
      <div className="space-y-4">
        {appointments.length > 0 ? (
          appointments.map(app => (
            <div key={app.id} className="bg-gradient-to-br from-indigo-500 to-blue-600 dark:from-indigo-600 dark:to-blue-700 p-6 rounded-3xl text-white relative shadow-lg shadow-blue-100 dark:shadow-none transition-all">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-80">การนัดหมายที่กำลังจะมาถึง</p>
                  </div>
                  <p className="text-2xl font-black">{new Date(app.date).toLocaleDateString('th-TH', { dateStyle: 'long' })}</p>
                  <p className="text-sm font-bold opacity-90 mt-1">{app.clinic} • {app.time} น.</p>
                  {app.notes && <p className="text-[11px] font-medium opacity-70 mt-1">*{app.notes}</p>}
                </div>
                <button onClick={() => handleDeleteApp(app.id)} className="bg-white/20 hover:bg-white/40 p-2 rounded-xl transition-all">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
            </div>
          ))
        ) : (
          !showAddApp && (
            <div className="bg-slate-50 dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 rounded-3xl text-center">
              <p className="text-xs font-bold text-slate-400 dark:text-slate-600">ไม่มีรายการนัดหมาย</p>
            </div>
          )
        )}
      </div>

      {/* Medication List */}
      <div className="space-y-3">
        {reminders.length > 0 ? (
          reminders.map(r => (
            <div key={r.id} className="bg-white dark:bg-slate-900 p-5 rounded-[32px] border border-slate-100 dark:border-slate-800 flex items-center justify-between shadow-sm hover:border-blue-200 dark:hover:border-blue-900 transition-all">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${r.active ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600'}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <p className={`font-bold transition-all ${r.active ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-600 line-through'}`}>{r.name}</p>
                  <p className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-tighter">{r.time} น. • {r.dosage || 'ระบุปริมาณ'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => handleDeleteMed(r.id)} className="text-slate-200 dark:text-slate-700 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={r.active} onChange={() => toggleMedActive(r.id, r.active)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-slate-200 dark:bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-200 after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>
            </div>
          ))
        ) : (
          !showAddMed && (
            <div className="bg-white/50 dark:bg-slate-900/50 p-8 rounded-3xl text-center border-2 border-dashed border-slate-100 dark:border-slate-800">
              <p className="text-xs font-bold text-slate-300 dark:text-slate-700">ยังไม่มีรายการแจ้งเตือนทานยา</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Alerts;
